import { useCallback, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let messageSeq = 0;
const nextMessageId = () => `nexo-msg-${++messageSeq}-${Date.now()}`;

const GENERIC_ERROR =
  "No pudimos enviar tu mensaje. Intenta de nuevo o escríbenos por WhatsApp.";
const EXPIRED_ERROR =
  "Esta conversación ya no está disponible. Escribe de nuevo para empezar otra.";

export default function useNexoChat() {
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [conversationClosed, setConversationClosed] = useState(false);
  const [showWhatsappHandoff, setShowWhatsappHandoff] = useState(false);
  const [messagesRemaining, setMessagesRemaining] = useState(null);
  // Scoped to this hook's mount lifetime only (a page reload or navigating
  // to a different page starts a brand new conversation). There is no
  // endpoint to refetch a past conversation's transcript, so persisting
  // this id across a remount would silently continue a conversation
  // server-side (possibly one the backend already considers closed) behind
  // an empty, misleading UI — the visitor would see a blank chat with no
  // way to tell it isn't new.
  const conversationIdRef = useRef(null);
  const lastUserMessageRef = useRef(null);

  const post = useCallback(async (text) => {
    setSending(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API}/assistant/chat`,
        {
          conversation_id: conversationIdRef.current || undefined,
          message: text,
          entry_url: window.location.href,
          referrer: document.referrer || undefined,
        },
        { timeout: 25000 }
      );
      conversationIdRef.current = data.conversation_id;
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: "assistant", content: data.message },
      ]);
      setConversationClosed(data.conversation_closed);
      setShowWhatsappHandoff(data.show_whatsapp_handoff);
      setMessagesRemaining(data.messages_remaining);
    } catch (err) {
      if (err?.response?.status === 409) {
        conversationIdRef.current = null;
        setError(EXPIRED_ERROR);
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setSending(false);
    }
  }, []);

  const sendMessage = useCallback(
    (text) => {
      const trimmed = text.trim();
      if (!trimmed || sending || conversationClosed) return;
      lastUserMessageRef.current = trimmed;
      setMessages((prev) => [
        ...prev,
        { id: nextMessageId(), role: "user", content: trimmed },
      ]);
      post(trimmed);
    },
    [post, sending, conversationClosed]
  );

  const retry = useCallback(() => {
    if (!lastUserMessageRef.current || sending) return;
    post(lastUserMessageRef.current);
  }, [post, sending]);

  const resetConversation = useCallback(() => {
    conversationIdRef.current = null;
    lastUserMessageRef.current = null;
    setMessages([]);
    setError(null);
    setConversationClosed(false);
    setShowWhatsappHandoff(false);
    setMessagesRemaining(null);
  }, []);

  return {
    messages,
    sending,
    error,
    conversationClosed,
    showWhatsappHandoff,
    messagesRemaining,
    sendMessage,
    retry,
    resetConversation,
  };
}
